import { useState, useCallback } from 'react'
import { getEmpsExcelAllAPI } from '../services/employee'
import SwalErrorCustom from '../utils/swal-error'

const DEFAULT_FILENAME = 'PIM_Export_Excel_Full.xlsx'

// Same parsing shape as use-export-report-attendance-summary.js's helper — content-disposition
// filenames are quoted/percent-encoded the same way across export endpoints in this app.
const parseFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i)
  if (!match?.[1]) return null

  try {
    return decodeURIComponent(match[1].replace(/['"]/g, ''))
  } catch {
    return match[1].replace(/['"]/g, '')
  }
}

/**
 * Downloads the PIM "Export Excel Full" file. Backend streams a real .xlsx (Meegle 14443031) — no
 * client-side workbook generation here, only the fetch + save-as-file wiring.
 *
 * `apiFn`/`defaultFilename` let the regular "ข้อมูลพนักงาน" export (Meegle 14443031 follow-up,
 * see use-export-pim-employee-excel-regular.js) reuse this exact fetch/save-as-file/isExporting
 * wiring instead of duplicating it — only the endpoint and fallback filename differ.
 */
export default function useExportPimEmployeeExcel({
  apiFn = getEmpsExcelAllAPI,
  defaultFilename = DEFAULT_FILENAME
} = {}) {
  const [isExporting, setIsExporting] = useState(false)

  const exportPimEmployeeExcel = useCallback(async (params) => {
    setIsExporting(true)
    try {
      const response = await apiFn(params)
      const blob = response?.data

      if (!(blob instanceof Blob) || blob.size === 0) {
        SwalErrorCustom(new Error('Export returned no file'))
        return false
      }

      const filename =
        parseFilenameFromContentDisposition(response?.headers?.['content-disposition']) ??
        defaultFilename

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      return true
    } catch (error) {
      SwalErrorCustom(error)
      return false
    } finally {
      setIsExporting(false)
    }
  }, [apiFn, defaultFilename])

  return { exportPimEmployeeExcel, isExporting }
}
